import { useMemo, useRef, useState } from 'react'

import { errorMessage } from '@/api/client'
import {
  ACTION_COPY,
  ACTION_TONE,
  formatWorklistEffort,
  useExportRun,
  useMatter,
  usePipelineRun,
  usePipelineRuns,
  useSignRun,
} from '@/api/pipeline'
import {
  WORKLIST_ACTIONS,
  jurisdictionLabel,
  proceedingLabel,
  type PipelineRun,
  type WorklistItem,
} from '@/api/types'
import { DraftView } from '@/components/DraftView'
import { Badge, Button, Card, CardHeader, EmptyState, ScreenHeader, cn } from '@/components/ui'
import { useRouter } from '@/routes'
import { useForgetMissingMatter } from '@/useForgetMissingMatter'
import { parseCasePath } from '@/screens/Case/casePath'
import { LoadingLine, RunErrorCard } from '@/screens/Case/runParts'

/**
 * Workbench — chunk 7, the v2.1a exit's narrower, buildable slice.
 *
 * The full plan's Workbench (`docs/IMPLEMENTATION_PLAN.md` §5 P5) is a strategy
 * tab, steered drafting with prompt variants, style exemplars, an elements
 * kanban and a per-jurisdiction final editor with true locked regions —
 * "~2–3 sessions" on its own. The published Build Docket scopes this chunk
 * down to four things, and this screen is exactly those four, no more:
 *
 * 1. the worklist as the **working panel** — STOP items disable export,
 *    and an `[ATTORNEY:]` marker is a jump target ({@link WorkingWorklist});
 * 2. the draft, **locked** — this build has no free-text edit endpoint (see
 *    `server/app/services/sign.py`'s module docstring for exactly why export
 *    stops short of a full gate re-run as a result), so "locked" is not a
 *    missing feature description, it is what is actually true today
 *    ({@link LockedDraft});
 * 3. **approve & sign**, human-only server-side, re-verifying the deadline
 *    at signature time ({@link SignExportBar});
 * 4. **export**, refused unless the run is signed and still gate-clean.
 *
 * "Clears items as resolved" (the docket's own phrase) is a **session-only**
 * UI affordance here, not a stored fact: clicking a marker's jump link marks
 * it reviewed in this tab only. Nothing is written for it — the only way a
 * STOP or a marker actually leaves the worklist is a clean re-run, because
 * claiming otherwise would misrepresent I4's guarantee.
 */
export function WorkbenchScreen() {
  const router = useRouter()
  const { matterId, runId: pinnedRunId } = useMemo(
    () => parseCasePath(router.path),
    [router.path],
  )

  const matter = useMatter(matterId)
  useForgetMissingMatter(matterId, matter)
  const runs = usePipelineRuns(matterId)
  const selectedRunId = pinnedRunId ?? runs.data?.[0]?.id ?? null
  const run = usePipelineRun(selectedRunId)

  if (!matterId) {
    return (
      <EmptyState
        title="No matter selected"
        description="The Workbench signs and exports one run at a time. Open a matter from Case or the Queue first."
      >
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => router.navigate('/queue')}>
            Go to Queue
          </Button>
        </div>
      </EmptyState>
    )
  }

  // `??` because the type is closed but the server's is not: a proceeding this
  // build has not heard of should print its own code, not "undefined".
  const kicker = matter.data
    ? `${proceedingLabel(matter.data.proceeding_type)} · ${jurisdictionLabel(matter.data.jurisdiction)}`
    : 'matter'
  const caseHref = router.href(
    selectedRunId ? `/case/${matterId}/${selectedRunId}` : `/case/${matterId}`,
  )

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        kicker={`Workbench · ${kicker}`}
        title={matterId}
        description="Sign the run a person has reviewed, then export the letterhead .docx it renders."
        actions={
          <a href={caseHref} className="text-meta text-brand-700 no-underline hover:underline">
            full run report →
          </a>
        }
      />

      {matter.isError ? (
        <RunErrorCard title="Could not load this matter" error={matter.error} />
      ) : null}

      {runs.isSuccess && (runs.data ?? []).length === 0 ? (
        <EmptyState
          title="This matter has never been run"
          description="Start a run from Case or New case before there is anything here to sign."
        />
      ) : null}

      {run.isPending && selectedRunId ? (
        <Card>
          <LoadingLine label="Loading the run…" />
        </Card>
      ) : null}
      {run.isError ? <RunErrorCard title="Could not load this run" error={run.error} /> : null}
      {run.data ? <WorkbenchBody run={run.data} /> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The working panel
// ---------------------------------------------------------------------------

function WorkbenchBody({ run }: { run: PipelineRun }) {
  const markerRefs = useRef<Record<string, HTMLElement | null>>({})
  const [reviewed, setReviewed] = useState<ReadonlySet<string>>(new Set())

  function jumpTo(detail: string) {
    const el = markerRefs.current[detail]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    setReviewed((prev) => new Set(prev).add(detail))
  }

  return (
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[380px_1fr]">
      <div className="flex flex-col gap-4">
        <SignExportBar run={run} />
        <WorkingWorklist run={run} reviewed={reviewed} onJump={jumpTo} />
      </div>
      <LockedDraft run={run} markerRefs={markerRefs} />
    </div>
  )
}

/**
 * Approve & sign, then export — in that order, and the second is disabled
 * until the first succeeds. `run.signed` is the server's own derived answer
 * ({@link PipelineRun.signed}), so a page reloaded after signing shows the
 * correct state without this component tracking anything itself.
 */
function SignExportBar({ run }: { run: PipelineRun }) {
  const sign = useSignRun()
  const exportRun = useExportRun()

  async function handleExport() {
    const { blob, filename } = await exportRun.mutateAsync(run.id)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Card role="group" aria-label="Sign and export" className="gap-3">
      <CardHeader
        title="Sign & export"
        description="A person attests this draft may leave the building; export re-checks the gate and renders the letterhead .docx."
      />
      <div className="flex flex-col gap-2">
        <Button
          variant="primary"
          disabled={!run.exportable || run.signed}
          loading={sign.isPending}
          loadingLabel="Signing…"
          onClick={() => sign.mutate(run.id)}
        >
          {run.signed ? 'Signed' : 'Approve & sign'}
        </Button>
        {!run.exportable ? (
          <p className="m-0 text-micro text-muted">
            Disabled — something on the list still has to be resolved before this can be sent. Clear it, then run the matter again.
          </p>
        ) : null}
        {sign.isError ? (
          <p role="alert" className="m-0 text-micro text-block-fg">
            {errorMessage(sign.error)}
          </p>
        ) : null}

        <Button
          variant="secondary"
          disabled={!run.signed}
          loading={exportRun.isPending}
          loadingLabel="Exporting…"
          onClick={() => void handleExport()}
        >
          Export .docx
        </Button>
        {!run.signed && run.exportable ? (
          <p className="m-0 text-micro text-muted">
            Disabled — sign first. Export needs a signed run, not merely a clean one.
          </p>
        ) : null}
        {exportRun.isError ? (
          <p role="alert" className="m-0 text-micro text-block-fg">
            {errorMessage(exportRun.error)}
          </p>
        ) : null}
      </div>
    </Card>
  )
}

/**
 * The worklist, as the screen's primary panel rather than one card among
 * several (`CaseScreen`'s `WorklistCard`, which this mirrors the rendering
 * of). The one addition: an `ATTORNEY_MARKER` item is a jump target.
 */
function WorkingWorklist({
  run,
  reviewed,
  onJump,
}: {
  run: PipelineRun
  reviewed: ReadonlySet<string>
  onJump: (detail: string) => void
}) {
  const { worklist } = run
  const groups = WORKLIST_ACTIONS.map(
    (action) => [action, worklist.items.filter((item) => item.action === action)] as const,
  ).filter(([, items]) => items.length > 0)

  return (
    <Card role="group" aria-label="What needs doing before this goes out" className="gap-3">
      <CardHeader
        title="What needs doing before this goes out"
        description="Anything marked STOP has to be cleared before this can be sent. Click a marker to jump to it in the draft; ticking it here is just for this visit and is not saved."
      />
      {/* Under the header, not in its actions slot: this card sits in a narrow
          column, and an actions slot sized to the estimate left the title and
          description a ~110px strip that wrapped over six lines. */}
      {worklist.items.length > 0 ? (
        <p className="m-0 text-meta text-muted">
          Time to clear the list: <span>{formatWorklistEffort(worklist)}</span>
        </p>
      ) : null}

      {worklist.items.length === 0 ? (
        <p className="m-0 text-body text-muted">
          Nothing on the worklist. That is a conclusion, not a gap — the gate, the deadline and
          retrieval each reported nothing to act on.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map(([action, items]) => (
            <section key={action} className="flex flex-col gap-1.5">
              <h5 className="m-0 flex items-center gap-2 text-meta">
                <Badge tone={ACTION_TONE[action]}>{action}</Badge>
                <span className="font-normal text-muted">{ACTION_COPY[action]}</span>
              </h5>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {items.map((item) => (
                  <WorklistLine
                    key={`${item.code}:${item.detail}`}
                    item={item}
                    reviewed={reviewed.has(item.detail)}
                    onJump={onJump}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Card>
  )
}

function WorklistLine({
  item,
  reviewed,
  onJump,
}: {
  item: WorklistItem
  reviewed: boolean
  onJump: (detail: string) => void
}) {
  const jumpable = item.code === 'ATTORNEY_MARKER'

  return (
    <li
      className={cn(
        'flex flex-col gap-0.5 rounded-md border px-3 py-2',
        reviewed ? 'border-ok-line bg-ok-bg' : 'border-divider',
      )}
    >
      <span className="flex flex-wrap items-baseline gap-2 text-meta">
        {/* The rule's id is for whoever is debugging, so it is the tooltip. It
            stays on screen only when no sentence explains the line, so a row is
            never left as a bare literal. */}
        {item.gloss ? null : <code className="font-mono text-micro text-muted">{item.code}</code>}
        <span className="font-medium" title={item.code}>
          {item.detail}
        </span>
        {reviewed ? <Badge tone="ok">reviewed</Badge> : null}
        <span className="ml-auto text-micro text-muted" title={`Source: ${item.source}`}>
          {item.minutes} min
        </span>
      </span>
      {item.gloss ? <span className="text-micro text-muted">{item.gloss}</span> : null}
      {jumpable ? (
        <button
          type="button"
          className="mt-1 self-start bg-transparent p-0 text-micro text-brand-700 no-underline hover:underline"
          onClick={() => onJump(item.detail)}
        >
          jump to marker in draft
        </button>
      ) : null}
    </li>
  )
}

/**
 * The draft. Locked — see the module docstring for exactly what that means
 * in this build. `[ATTORNEY: …]` markers render as highlighted spans that
 * {@link WorkingWorklist}'s jump links scroll to.
 */
function LockedDraft({
  run,
  markerRefs,
}: {
  run: PipelineRun
  markerRefs: React.MutableRefObject<Record<string, HTMLElement | null>>
}) {
  if (!run.draft) {
    return (
      <Card role="group" aria-label="Draft" className="gap-2">
        <CardHeader title="Draft" description="There is no draft on this run." />
      </Card>
    )
  }

  return (
    <Card role="group" aria-label="Draft" className="gap-3">
      <CardHeader
        title="Draft"
        description="Locked — there is no text editor on this screen. Correct the source document and run the matter again to change it."
        actions={
          run.exportable ? (
            <Badge tone="ok">exportable</Badge>
          ) : (
            <Badge tone="block">not exportable</Badge>
          )
        }
      />
      <DraftView
        run={run}
        maxHeightClass="max-h-[640px]"
        renderMarker={(marker, key) => (
          <mark
            key={key}
            ref={(el) => {
              markerRefs.current[marker] = el
            }}
            className="rounded bg-warn-bg px-0.5 text-warn-fg"
          >
            {marker}
          </mark>
        )}
      />
    </Card>
  )
}
